import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";

const CONSENT_KEY = "maip_cookie_consent";
const CONSENT_TIMESTAMP_KEY = "maip_consent_timestamp";
const CONSENT_VERSION = "1.0";

export type ConsentSettings = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  version: string;
  timestamp: string;
};

export const hasConsent = (type: keyof Omit<ConsentSettings, 'version' | 'timestamp'>): boolean => {
  const consent = localStorage.getItem(CONSENT_KEY);
  if (!consent) return false;
  
  try {
    const settings: ConsentSettings = JSON.parse(consent);
    return settings[type] === true;
  } catch {
    return false;
  }
};

export const getConsentSettings = (): ConsentSettings | null => {
  const consent = localStorage.getItem(CONSENT_KEY);
  if (!consent) return null;
  
  try {
    return JSON.parse(consent);
  } catch {
    return null;
  }
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [settings, setSettings] = useState<ConsentSettings>({
    necessary: true,
    functional: false,
    analytics: false,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  });

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    const consentTimestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
    
    if (!consent || !consentTimestamp) {
      setShowBanner(true);
    } else {
      try {
        const savedSettings: ConsentSettings = JSON.parse(consent);
        if (savedSettings.version !== CONSENT_VERSION) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (consentSettings: ConsentSettings) => {
    const consentData = {
      ...consentSettings,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, consentData.timestamp);
    setShowBanner(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    });
  };

  const saveCustomSettings = () => {
    saveConsent(settings);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t shadow-lg">
      <Card className="max-w-5xl mx-auto p-6">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">Cookie-Einstellungen</h3>
            
            {!showDetails ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Wir respektieren Ihre Privatsphäre. Für die grundlegende Funktion unserer Webseite nutzen wir keine zustimmungspflichtigen Cookies. 
                  Um Ihnen jedoch interaktive Inhalte wie Videos (YouTube) und Karten (Google Maps) anzeigen zu können, benötigen wir Ihre Einwilligung. 
                  Erst nach Ihrer Zustimmung wird eine Verbindung zu den Servern von Google hergestellt.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={acceptAll} size="sm">
                    Allen zustimmen
                  </Button>
                  <Button onClick={acceptNecessary} variant="outline" size="sm">
                    Nur Notwendiges akzeptieren
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4 mb-4">
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <input
                      type="checkbox"
                      checked={settings.necessary}
                      disabled
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Notwendige Cookies</p>
                      <p className="text-xs text-muted-foreground">
                        Erforderlich für Login, Session-Verwaltung und Sicherheitsfunktionen. 
                        Können nicht deaktiviert werden.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <input
                      type="checkbox"
                      checked={settings.functional}
                      onChange={(e) => setSettings({ ...settings, functional: e.target.checked })}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Funktionale Cookies</p>
                      <p className="text-xs text-muted-foreground">
                        Speichern Ihre Präferenzen (Branche, Themen) für personalisierte Inhaltsempfehlungen.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <input
                      type="checkbox"
                      checked={settings.analytics}
                      onChange={(e) => setSettings({ ...settings, analytics: e.target.checked })}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Analytics-Cookies</p>
                      <p className="text-xs text-muted-foreground">
                        Helfen uns zu verstehen, wie Sie die Plattform nutzen (derzeit nicht aktiv).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button onClick={saveCustomSettings} size="sm">
                    Auswahl speichern
                  </Button>
                  <Button 
                    onClick={() => setShowDetails(false)} 
                    variant="outline" 
                    size="sm"
                  >
                    Zurück
                  </Button>
                </div>
              </>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={acceptNecessary}
            className="flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Weitere Informationen finden Sie in unserer{" "}
            <a href="/datenschutz" className="underline hover:text-foreground">
              Datenschutzerklärung
            </a>
            {" "}und im{" "}
            <a href="/impressum" className="underline hover:text-foreground">
              Impressum
            </a>
            .
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
